/**
 * Module 00 — Global Shell · Sidebar (FE-00-02, FE-00-08, FE-00-09)
 *
 * 10-group rail with collapse (64px icon rail), sub-menus, parent+child active
 * state, org switcher, and a credits meter footer. RBAC-by-hiding is applied by
 * passing a capability-filtered `nav` in (see `lib/shell/nav.ts`). Pure props
 * (`route` / `go`) so it's deterministic to test (G-01, G-02, G-03, G-10).
 */

'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

import { useAuth } from '@/lib/hooks/useAuth';
import { Icon } from './icons';
import { ModeSwitch } from './ModeSwitch';
import { pathForRoute } from '@/lib/shell/useShellNav';
import {
  NAV as DEFAULT_NAV,
  type NavGroup,
  type NavItem,
} from '@/lib/shell/nav';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/custom';
import { identityService } from '@/lib/api/identity';
import { projectsService } from '@/lib/api/services/projects';
import { CreateDepartmentModal } from './CreateDepartmentModal';
import { SignedInAppsDialog } from './SignedInAppsDialog';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { authService } from '@/lib/api/auth';
import { useScopeStore } from '@/lib/stores/scope-store';
import { NavCountsContext, useNavCounts } from '@/lib/shell/nav-badges';
import type { Theme, Mode } from '@/lib/shell/persistence';
import { type RippleOrigin, rippleOrigin } from '@/lib/shell/themeRipple';
import type { ScopeRef, OrgRef, Credits } from '@/lib/api/services/session';
import { WexaLogo } from './WexaLogo';

export interface SidebarProps {
  route: string;
  go: (route: string) => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  nav?: NavGroup[];
  org?: OrgRef | null;
  credits?: Credits | null;
  /** Theme + mode controls, relocated from the top bar to the footer. */
  theme?: Theme;
  toggleTheme?: (origin?: RippleOrigin) => void;
  mode?: Mode;
  setMode?: (m: Mode) => void;
  /** Scope (dept/project) switchers, relocated into the org card. */
  department?: ScopeRef | null;
  project?: ScopeRef | null;
  availableDepartments?: ScopeRef[];
  availableProjects?: ScopeRef[];
  /** Every org the user belongs to; >1 turns the org row into a switcher. */
  availableOrgs?: OrgRef[];
  onSelectDepartment?: (d: ScopeRef) => void;
  onSelectProject?: (p: ScopeRef) => void;
  onSelectOrg?: (o: OrgRef) => void;
  /** Gates the "New department" action in the department switcher (RBAC-by-hiding). */
  canCreateDepartment?: boolean;
  /** Gates the "New project" action. A project_admin administers the project they
   *  were given, they don't create new ones — and a member can't either, so
   *  offering the action just leads to a rejected call. */
  canCreateProject?: boolean;
  /** Initials for the footer avatar. */
  userInitials?: string;
  userName?: string;
  userEmail?: string;
  /** Tooltip on the footer avatar (user menu). */
  userRole?: string;
  /** Gates the credits meter + Upgrade (billing.view). */
  showCredits?: boolean;
  /** Onboarding gate: disable all module nav items until a project exists. */
  navDisabled?: boolean;
  /** Opens the global command-palette search from the sidebar header. */
  onOpenSearch?: () => void;
}

export function SbItem({
  it,
  route,
  go,
  collapsed,
  disabled = false,
}: {
  it: NavItem;
  route: string;
  go: (route: string) => void;
  collapsed: boolean;
  disabled?: boolean;
}) {
  const router = useRouter();
  const hasChildren = !!it.children && it.children.length > 0;
  const childActive = hasChildren && it.children!.some((c) => c.route === route);
  const isActive = route === it.route;
  const [open, setOpen] = React.useState(childActive);
  const counts = React.useContext(NavCountsContext);
  const live = counts[it.route];
  const childLive = hasChildren ? it.children!.reduce((n, c) => n + (counts[c.route]?.count ?? 0), 0) : 0;

  // Warm the route (JS chunk + RSC payload) before the click lands so the
  // navigation itself is near-instant in production. In `next dev`, however,
  // router.prefetch eagerly COMPILES the hovered route on-demand; with the
  // single dev compiler (and slow polling-mode rebuilds) a heavy route like the
  // NVL graph can take seconds and stall the actual navigation the user clicked.
  // So prefetch in production only — dev navigates fine without warm chunks.
  const prefetch = (r: string) => {
    if (process.env.NODE_ENV !== 'production') return;
    router.prefetch(pathForRoute(r));
  };

  return (
    <>
      <button
        type="button"
        data-testid={`nav-${it.route}`}
        title={collapsed ? it.label : undefined}
        // Collapsed, the label span is hidden, so the button would otherwise
        // have no accessible name at all.
        aria-label={collapsed ? it.label : undefined}
        aria-current={isActive || childActive ? 'page' : undefined}
        aria-expanded={hasChildren ? open : undefined}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        onMouseEnter={disabled ? undefined : () => {
          if (hasChildren) it.children!.forEach((c) => prefetch(c.route));
          else prefetch(it.route);
        }}
        onClick={disabled ? undefined : () => (hasChildren ? setOpen((o) => !o) : go(it.route))}
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-cs-text-secondary transition-colors',
          disabled
            ? 'cursor-not-allowed opacity-40'
            : 'hover:bg-cs-surface-hover hover:text-cs-text-primary',
          collapsed && 'justify-center px-0',
          !disabled && (isActive || childActive) && !collapsed
            ? 'bg-cs-primary-500/10 text-cs-primary-400 font-medium border-l-2 border-cs-primary-500 pl-[9px]'
            : !disabled && (isActive || childActive) && collapsed
            ? 'bg-cs-primary-500/10 text-cs-primary-400 font-medium'
            : '',
        )}
      >
        <span className="relative flex-shrink-0">
          <Icon name={it.icon} size={18} />
          {collapsed && (live || childLive > 0) && (
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-cs-primary-500" aria-hidden="true" />
          )}
        </span>
        {!collapsed && <span className="flex-1 truncate text-left">{it.label}</span>}
        {!collapsed && (live || (childLive > 0 && !open)) && (
          <span
            className="min-w-[18px] rounded-full bg-cs-primary-500 px-1.5 text-center text-[10px] font-semibold leading-[18px] text-white"
            title={live?.title}
            aria-label={live?.title}
            data-testid={`nav-count-${it.route}`}
          >
            {live?.count ?? childLive}
          </span>
        )}
        {!collapsed && it.badge && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-medium',
              it.badge === 'Upgrade'
                ? 'bg-cs-primary-500/15 text-cs-primary-400'
                : 'bg-cs-surface-raised text-cs-text-muted',
            )}
          >
            {it.badge}
          </span>
        )}
        {!collapsed && hasChildren && (
          <Icon
            name={open ? 'chevup' : 'chevdown'}
            size={13}
            className="ml-auto text-cs-text-muted"
          />
        )}
      </button>

      {!collapsed && hasChildren && open && (
        <div className="ml-3 flex flex-col gap-0.5 border-l border-cs-border pl-2">
          {it.children!.map((ch) => (
            <button
              key={ch.route}
              type="button"
              data-testid={`subnav-${ch.route}`}
              aria-current={route === ch.route ? 'page' : undefined}
              onMouseEnter={() => prefetch(ch.route)}
              onClick={() => go(ch.route)}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] text-cs-text-secondary transition-colors',
                'hover:bg-cs-surface-hover hover:text-cs-text-primary',
                route === ch.route
                  ? 'bg-cs-primary-500/10 text-cs-primary-400 font-medium border-l-2 border-cs-primary-500 pl-[9px]'
                  : '',
              )}
            >
              <Icon name={ch.icon} size={15} className="flex-shrink-0" />
              <span className="flex-1 truncate text-left">{ch.label}</span>
              {counts[ch.route] && (
                <span
                  className="min-w-[18px] rounded-full bg-cs-primary-500 px-1.5 text-center text-[10px] font-semibold leading-[18px] text-white"
                  title={counts[ch.route].title}
                  aria-label={counts[ch.route].title}
                  data-testid={`nav-count-${ch.route}`}
                >
                  {counts[ch.route].count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Quiet dropdown switcher (moved here from the top bar). Click-outside + Esc
 * close, a confirmation toast on select, and a checkmark on the active option.
 */
function ScopeSwitcher({
  testid,
  icon,
  value,
  options,
  onSelect,
  label,
  onCreate,
  createLabel,
}: {
  testid: string;
  icon?: string;
  value?: ScopeRef | null;
  options: ScopeRef[];
  onSelect: (o: ScopeRef) => void;
  label: string;
  onCreate?: () => void;
  createLabel?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testid}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-sm text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary"
      >
        {icon && <Icon name={icon} size={14} className="flex-shrink-0 text-cs-text-muted" />}
        <span className="flex-1 truncate text-left">{value?.name ?? '—'}</span>
        <Icon name="chevdown" size={13} className="text-cs-text-muted" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`${testid}-menu`}
          className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-lg border border-cs-border bg-cs-surface-card p-1 shadow-2xl"
        >
          {options.length === 0 && (
            <div className="px-3 py-2 text-xs text-cs-text-muted">No options</div>
          )}
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitem"
              data-testid={`${testid}-opt-${o.id}`}
              onClick={() => {
                onSelect(o);
                toast.success(`Scope updated — ${o.name}`);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary',
                o.id === value?.id && 'text-cs-text-primary',
              )}
            >
              <span className="truncate">{o.name}</span>
              {o.id === value?.id && <Icon name="check" size={14} className="text-cs-primary-400" />}
            </button>
          ))}
          {onCreate && (
            <>
              <div className="my-1 h-px bg-cs-border" />
              <button
                type="button"
                role="menuitem"
                data-testid={`${testid}-create`}
                onClick={() => { setOpen(false); onCreate(); }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm font-medium text-cs-primary-400 transition-colors hover:bg-cs-surface-hover"
              >
                <Icon name="plus" size={14} />
                {createLabel ?? 'New'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Combined scope card: the org row (still routes to org settings) plus the
 * Department › Project switchers and the New-project action — all relocated
 * from the old top bar so the sidebar is the single place to change scope.
 */
/**
 * Org row as a switcher, shown only when the user belongs to more than one org
 * (they accepted an invite to a second one). Switching re-scopes the whole
 * shell, so it reloads rather than patching state: departments, projects,
 * capabilities and every cached query belong to the org being left.
 */
function OrgSwitcher({
  org,
  options,
  onSelect,
  go,
}: {
  org?: OrgRef | null;
  options: OrgRef[];
  onSelect: (o: OrgRef) => void;
  go: (route: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
          type="button"
          data-testid="org-switcher"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Switch organisation"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-cs-surface-hover" title="Organization settings"
        >
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-cs-primary-500/15 text-cs-primary-400">
            <Icon name="org" size={15} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-cs-text-primary">
              {org?.name ?? 'Organization'}
            </div>
            <div className="truncate text-[11px] text-cs-text-muted">
              {org?.plan ? `${org.plan} plan` : '—'}
            </div>
          </div>
          <Icon name="chevdown" size={14} className="text-cs-text-muted" />
        </button>
      {open && (
        <div
          role="menu"
          data-testid="org-switcher-menu"
          className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-lg border border-cs-border bg-cs-surface-card p-1 shadow-2xl"
        >
          <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-cs-text-muted">
            Your organisations
          </div>
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitem"
              data-testid={`org-switcher-opt-${o.id}`}
              onClick={() => {
                setOpen(false);
                if (o.id !== org?.id) onSelect(o);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary',
                o.id === org?.id && 'text-cs-text-primary',
              )}
            >
              <span className="truncate">{o.name}</span>
              {o.id === org?.id && <Icon name="check" size={14} className="text-cs-primary-400" />}
            </button>
          ))}
          <div className="my-1 h-px bg-cs-border" />
          <button
            type="button"
            role="menuitem"
            data-testid="org-switcher-settings"
            onClick={() => { setOpen(false); go('settings/org'); }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary"
          >
            <Icon name="settings" size={14} />
            Organisation settings
          </button>
        </div>
      )}
    </div>
  );
}

function ScopeCard({
  org,
  department,
  project,
  availableDepartments,
  availableProjects,
  availableOrgs,
  onSelectDepartment,
  onSelectProject,
  onSelectOrg,
  onNewProject,
  onNewDepartment,
  collapsed,
  go,
}: {
  org?: OrgRef | null;
  department?: ScopeRef | null;
  project?: ScopeRef | null;
  availableDepartments: ScopeRef[];
  availableProjects: ScopeRef[];
  availableOrgs: OrgRef[];
  onSelectDepartment: (d: ScopeRef) => void;
  onSelectProject: (p: ScopeRef) => void;
  onSelectOrg: (o: OrgRef) => void;
  onNewProject?: () => void;
  onNewDepartment?: () => void;
  collapsed: boolean;
  go: (route: string) => void;
}) {
  const hasDepartment = !!department?.id;
  // Belonging to a second org only happens by accepting an invite to one, so
  // most users never see a switcher — the row keeps opening org settings.
  const canSwitchOrg = availableOrgs.length > 1;

  return (
    <div
      className={cn(
        'rounded-lg border border-cs-border bg-cs-surface-raised',
        collapsed ? 'p-0' : 'p-1.5',
      )}
    >
      {/* org row → switch org when they belong to several, else org settings */}
      {canSwitchOrg && !collapsed ? (
        <OrgSwitcher org={org} options={availableOrgs} onSelect={onSelectOrg} go={go} />
      ) : (
      <button
        type="button"
        data-testid="org-switcher"
        onClick={() => go('settings/org')}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-cs-surface-hover',
          collapsed && 'justify-center px-0 py-2',
        )}
      >
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-cs-primary-500/15 text-cs-primary-400">
          <Icon name="org" size={15} />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-cs-text-primary">
              {org?.name ?? 'Organization'}
            </div>
            <div className="truncate text-[11px] text-cs-text-muted">
              {org?.plan ? `${org.plan} plan` : '—'}
            </div>
          </div>
        )}
        {!collapsed && <Icon name="chevright" size={14} className="text-cs-text-muted" />}
      </button>
      )}

      {/* dept / project switchers — always show department so an empty org can
          still create/select one (hiding behind hasDepartment left the row
          permanently missing when bootstrap returned no active department). */}
      {!collapsed && (
        <div className="mt-1 flex flex-col gap-0.5 border-t border-cs-border pt-1">
          <ScopeSwitcher
            testid="dept-switcher"
            icon="departments"
            value={hasDepartment ? department : { id: '', name: 'Select department' }}
            options={availableDepartments}
            onSelect={onSelectDepartment}
            label="Switch department"
            onCreate={org?.id ? onNewDepartment : undefined}
            createLabel="New department"
          />
          <ScopeSwitcher
            testid="project-switcher"
            icon="projects"
            value={project}
            options={availableProjects}
            onSelect={onSelectProject}
            label="Switch project"
            onCreate={org?.id ? onNewProject : undefined}
            createLabel="New project"
          />
        </div>
      )}
    </div>
  );
}

function CreditsMeter({
  credits,
  go,
}: {
  credits?: Credits | null;
  go: (route: string) => void;
}) {
  const total = credits?.total ?? 0;
  const used = credits?.used ?? 0;
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
  const hasData = total > 0;

  return (
    <div
      data-testid="credits-meter"
      className="rounded-lg border border-cs-border bg-cs-surface-raised p-3"
    >
      <div className="mb-1.5 flex items-center justify-between text-xs">
        <span className="text-cs-text-muted">Credits left</span>
        <b className="text-cs-text-primary">{hasData ? remaining.toLocaleString() : '—'}</b>
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-cs-surface-hover"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span
          className="block h-full rounded-full bg-cs-primary-500 transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <button
        type="button"
        data-testid="upgrade-btn"
        onClick={() => go('settings/billing')}
        className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-md bg-cs-primary-500 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-cs-primary-600"
      >
        <Icon name="crown" size={14} /> Upgrade plan
      </button>
    </div>
  );
}

/**
 * Avatar → user menu (relocated from the top bar to the sidebar footer; the old
 * footer avatar had no menu, so this is the only logout path). Opens upward so
 * the menu stays on-screen at the bottom of the rail. Log out is wired to the
 * AuthProvider, which clears the session (POST /auth/logout) and redirects to
 * /login. Settings is hidden for now — see FAB-206 below.
 */
function UserMenu({
  userInitials, userRole, children, collapsed,
}: {
  userInitials: string;
  userRole?: string;
  /** Name/email, rendered INSIDE the trigger so clicking them opens the menu. */
  children?: React.ReactNode;
  collapsed?: boolean;
}) {
  const { user, logout } = useAuth();
  // FAB-206: router only served the hidden Settings item — restore with it.
  // const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const [signingOut, setSigningOut] = React.useState(false);
  const [appsOpen, setAppsOpen] = React.useState(false);
  const [passwordOpen, setPasswordOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  // Single sign-on accounts have no Fabric password — their identity provider
  // owns it — so "Change password" is offered only once identity-service says
  // this account has one. Unknown (loading or failed) keeps it hidden.
  const passwordStatus = useQuery({
    queryKey: ['password-status', user?.email],
    queryFn: () => authService.getPasswordStatus(),
    enabled: !!user,
    staleTime: Infinity,
    retry: false,
    // A failure only hides the item. Never escalate it to the error boundary:
    // a signed-out session 401s here, and the shell's own 401 handling must
    // get to send it to /login.
    throwOnError: false,
  });

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onLogout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    setOpen(false);
    try {
      await logout();
    } catch {
      toast.error('Logout failed — please try again');
      setSigningOut(false);
    }
  };

  return (
    <div className={cn('relative', !collapsed && 'w-full')} ref={ref}>
      {/* FAB-332: the trigger is the WHOLE row, not just the avatar. The name and
          email used to sit in a sibling div, so clicking the obvious target — a
          person's own name — did nothing and the menu read as broken. */}
      <button title={userRole}
          type="button"
          data-testid="avatar"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="User menu"
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'flex items-center gap-2.5 text-left transition-colors',
            collapsed
              ? 'h-8 w-8 flex-shrink-0 justify-center rounded-full bg-cs-surface-raised text-xs font-semibold text-cs-text-primary hover:ring-2 hover:ring-cs-border'
              : 'w-full rounded-md p-1 hover:bg-cs-surface-hover',
          )}
        >
          <span
            className={cn(
              'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-cs-text-primary',
              !collapsed && 'bg-cs-surface-raised',
            )}
          >
            {userInitials}
          </span>
          {children}
        </button>
      {open && (
        <div
          role="menu"
          data-testid="user-menu"
          className="absolute bottom-full left-0 z-50 mb-1 min-w-[200px] rounded-lg border border-cs-border bg-cs-surface-card p-1 shadow-2xl"
        >
          {user?.email && (
            <div className="border-b border-cs-border px-3 py-2">
              <p className="truncate text-xs font-medium text-cs-text-primary">
                {user.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : (user.username ?? 'User')}
              </p>
              <p className="truncate text-[11px] text-cs-text-muted">{user.email}</p>
            </div>
          )}
          {/* FAB-206: Settings is hidden until the billing/usage module is ready —
              pricing, plan features and invoices are still hardcoded, so exposing
              it sets wrong expectations. Re-enable (with its divider) once the
              billing integration is complete. Its FAB-154 tooltip went with it —
              restore `content="Organisation, members, billing and policies"` when
              the button comes back.
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); router.push('/settings'); }}
            className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary"
          >
            <Icon name="sliders" size={14} className="text-cs-text-muted" />
            Settings
          </button>
          <div className="my-1 h-px bg-cs-border" />
          */}
          <button
            type="button"
            role="menuitem"
            data-testid="signed-in-apps-btn"
            onClick={() => { setOpen(false); setAppsOpen(true); }}
            className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary"
          >
            <Icon name="lock" size={14} className="text-cs-text-muted" />
            Signed-in apps
          </button>
          {passwordStatus.data?.hasPassword && (
            <button
              type="button"
              role="menuitem"
              data-testid="change-password-btn"
              onClick={() => { setOpen(false); setPasswordOpen(true); }}
              className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary"
            >
              <Icon name="apikeys" size={14} className="text-cs-text-muted" />
              Change password
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            data-testid="logout-btn"
            disabled={signingOut}
            onClick={onLogout}
            className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm text-cs-error-400 transition-colors hover:bg-cs-error-bg disabled:opacity-60"
          >
            <Icon name="logout" size={14} />
            {signingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>
      )}
      <SignedInAppsDialog open={appsOpen} onOpenChange={setAppsOpen} />
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </div>
  );
}

function CreateProjectModal({ open, onOpenChange, orgId }: { open: boolean; onOpenChange: (v: boolean) => void; orgId?: string }) {
  const qc = useQueryClient();
  const addProject = useScopeStore((s) => s.addProject);
  const deptId = useScopeStore((s) => s.department?.id);
  const availableDepartments = useScopeStore((s) => s.availableDepartments);
  const [name, setName] = React.useState('');
  const [selectedDeptId, setSelectedDeptId] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setName('');
      setError(null);
      setSelectedDeptId(deptId || availableDepartments[0]?.id || '');
    }
  }, [open, deptId, availableDepartments]);

  const submit = async () => {
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    if (!orgId) { setError('No organization in scope'); return; }
    setSaving(true);
    try {
      const created = await identityService.createProject({
        projectName: name.trim(),
        orgId,
        ...(selectedDeptId ? { departmentId: selectedDeptId } : {}),
      });
      const projectId = created?._id || created?.id;
      if (!projectId) throw new Error('Identity did not return a project id');

      // Mirror just the new project into harness; the full /auth/initialize
      // bootstrap re-synced every org and project on each create.
      await projectsService.syncProjects().catch(() => {});

      addProject({ id: String(projectId), name: name.trim() }); // activate the new project
      qc.invalidateQueries({ queryKey: ['shell', 'projects'] }); // refresh dept-scoped switcher
      toast.success(`Project created — ${name.trim()}`);
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.response?.status === 409 ? 'A project with that name already exists' : 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {availableDepartments.length > 0 && (
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold text-cs-text-muted">Department</label>
              <Select value={selectedDeptId} onValueChange={setSelectedDeptId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {availableDepartments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-[11px] font-semibold text-cs-text-muted">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Customer Success" aria-invalid={!!error} autoFocus
              onKeyDown={(e) => e.key === 'Enter' && submit()} />
            {error && <p role="alert" className="mt-1 text-[12px] text-cs-error-400">{error}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Sidebar({
  route,
  go,
  collapsed,
  setCollapsed,
  nav = DEFAULT_NAV,
  org,
  credits,
  theme = 'dark',
  toggleTheme = () => {},
  mode = 'advanced',
  setMode = () => {},
  department,
  project,
  availableDepartments = [],
  availableProjects = [],
  availableOrgs = [],
  onSelectDepartment = () => {},
  onSelectProject = () => {},
  onSelectOrg = () => {},
  canCreateDepartment = false,
  canCreateProject = false,
  userInitials = '—',
  userName,
  userEmail,
  userRole,
  showCredits = true,
  navDisabled = false,
  onOpenSearch = () => {},
}: SidebarProps) {
  const [createProjectOpen, setCreateProjectOpen] = React.useState(false);
  const [createDeptOpen, setCreateDeptOpen] = React.useState(false);
  const navCounts = useNavCounts(project?.id);
  return (
    <NavCountsContext.Provider value={navCounts}>
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed}
      className={cn(
        'flex h-full flex-col border-r border-cs-border bg-cs-surface-sidebar',
        collapsed ? 'w-16' : 'w-[252px]',
      )}
    >
      {/* brand + collapse toggle */}
      <div className={cn('flex items-center px-3 py-3', collapsed ? 'justify-center' : 'gap-1')}>
        {collapsed ? (
          <button
              type="button"
              data-testid="collapse-toggle"
              aria-label="Expand sidebar"
              onClick={() => setCollapsed(false)}
              className={cn(
                'group relative mx-auto flex h-8 w-8 items-center justify-center rounded-lg',
                'text-cs-text-primary transition-colors',
                'hover:bg-cs-surface-hover focus-visible:bg-cs-surface-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cs-accent/40',
              )} title={collapsed ? 'Expand' : 'Collapse'}
            >
              <span
                data-testid="sidebar-brand-mark"
                className="transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0"
              >
                <WexaLogo size={24} />
              </span>
              <Icon
                name="chevright"
                size={18}
                className="absolute text-cs-text-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              />
            </button>
        ) : (
          <>
            <button
              type="button"
              data-testid="sidebar-home"
              aria-label="Go to Home"
              onClick={() => go('home')}
              className={cn(
                'flex min-w-0 flex-1 cursor-pointer items-center gap-1 rounded-md px-0.5 py-0.5 text-left transition-colors',
                'hover:bg-cs-surface-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cs-accent/40',
              )}
            >
              <span
                data-testid="sidebar-brand-mark"
                className="shrink-0 text-cs-text-primary"
              >
                <WexaLogo size={24} />
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-cs-text-primary">
                Wexa Fabric
              </span>
            </button>
            <button
                type="button"
                data-testid="sidebar-search"
                aria-label="Search application"
                onClick={onOpenSearch}
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                  'text-cs-text-muted transition-colors',
                  'hover:bg-cs-surface-hover hover:text-cs-text-primary',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cs-accent/40',
                )}
              >
                <Icon name="search" size={16} />
              </button>
            <button
                type="button"
                data-testid="collapse-toggle"
                aria-label="Collapse sidebar"
                onClick={() => setCollapsed(true)}
                className="flex h-7 w-7 items-center justify-center rounded-md text-cs-text-muted transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary"
              >
                <Icon name="chevleft" size={16} />
              </button>
          </>
        )}
      </div>

      {/* scope card: org → dept → project */}
      <div className="px-3 pb-2">
        <ScopeCard
          org={org}
          department={department}
          project={project}
          availableDepartments={availableDepartments}
          availableProjects={availableProjects}
          availableOrgs={availableOrgs}
          onSelectDepartment={onSelectDepartment}
          onSelectProject={onSelectProject}
          onSelectOrg={onSelectOrg}
          onNewProject={canCreateProject ? () => setCreateProjectOpen(true) : undefined}
          onNewDepartment={canCreateDepartment ? () => setCreateDeptOpen(true) : undefined}
          collapsed={collapsed}
          go={go}
        />
      </div>

      {/* scrollable nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {nav.map((g: NavGroup) => (
          <div key={g.group} className="mb-3">
            <div
              data-sidebar-group-separator={collapsed ? '' : undefined}
              aria-hidden={collapsed || undefined}
              className={cn(
                'mb-1',
                collapsed
                  ? 'mx-1 h-px bg-cs-border'
                  : 'px-2 text-[10px] font-semibold uppercase tracking-wider text-cs-text-muted',
              )}
            >
              {!collapsed && g.group}
            </div>
            <div className="flex flex-col gap-0.5">
              {g.items.map((it) => (
                <SbItem key={it.route} it={it} route={route} go={go} collapsed={collapsed} disabled={navDisabled} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* footer: credits + controls (theme/help/mode) + user */}
      <div className="flex flex-col gap-2 border-t border-cs-border p-3">
        {!collapsed && showCredits && <CreditsMeter credits={credits} go={go} />}

        {/* controls row (relocated from the top bar) */}
        <div className={cn('flex items-center', collapsed ? 'flex-col gap-1' : 'justify-between')}>
          <div className={cn('flex items-center gap-1', collapsed && 'flex-col')}>
            <button
                type="button"
                data-testid="theme-toggle"
                aria-label="Toggle theme"
                onClick={(e) => toggleTheme(rippleOrigin(e.currentTarget))}
                className="flex h-8 w-8 items-center justify-center rounded-md text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary" title="Toggle theme"
              >
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
              </button>
            <button
                type="button"
                data-testid="help-btn"
                aria-label="Documentation"
                onClick={() => go('docs')}
                className="flex h-8 w-8 items-center justify-center rounded-md text-cs-text-secondary transition-colors hover:bg-cs-surface-hover hover:text-cs-text-primary" title="Documentation"
              >
                <Icon name="help" size={18} />
              </button>
          </div>
          <ModeSwitch mode={mode} setMode={setMode} compact={collapsed} />
        </div>

        {/* user identity + menu — the whole row is the menu trigger (FAB-332) */}
        <div className={cn('flex items-center', collapsed && 'justify-center')}>
          <UserMenu userInitials={userInitials} userRole={userRole} collapsed={collapsed}>
            {!collapsed && (
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-cs-text-primary">{userName ?? '—'}</span>
                <span className="block truncate text-[11px] text-cs-text-muted">{userEmail ?? ''}</span>
              </span>
            )}
          </UserMenu>
        </div>
      </div>

      <CreateProjectModal open={createProjectOpen} onOpenChange={setCreateProjectOpen} orgId={org?.id} />
      {/* New department: the modal calls addDepartment(), which appends the
          new dept to the switcher list and activates it (same path the
          onboarding gate uses) — no extra wiring needed here. */}
      <CreateDepartmentModal open={createDeptOpen} onOpenChange={setCreateDeptOpen} orgId={org?.id} />
    </aside>
    </NavCountsContext.Provider>
  );
}
